import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { allNews, allWorks } from "@/mocks";

const FORM_URL = "https://readdy.ai/api/form/dbadkpm9if4sq287d10g";

const VALUES = [
  { icon: "ri-book-open-line", title: "剧情优先", text: "以剧情信息为核心，为每部作品整理完整的剧情脉络与人物关系，让查阅更有收获。" },
  { icon: "ri-shield-check-line", title: "信息清晰", text: "所有作品都标注类型、地区、年份与播放状态，分类与筛选保持一致的资料标准。" },
  { icon: "ri-heart-3-line", title: "克制表达", text: "不做夸张标题党，把篇幅留给作品本身，用冷静的文字陪伴观众理解一部影视。" },
];

export default function About() {
  const [contactEmail, setContactEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const alt = String(formData.get("contact_alt") || "").trim();
    const payload = new URLSearchParams();

    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      payload.append(key, String(value));
    });

    if (alt) {
      setStatus("success");
      form.reset();
      return;
    }

    if (String(formData.get("message") || "").length > 500) {
      setStatus("error");
      setFormError("留言内容不能超过 500 字，请精简后再提交。");
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg: string =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
      setFormError("网络异常，留言提交失败，请稍后重试。");
    }
  };

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200/70 bg-background-100">
        <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-8 px-4 py-8 md:px-6 md:py-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-[34px]">
              关于「女友的妈妈」影视专区
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-[15px]">
              我们是一个专注影视剧情与行业资讯的内容门户。从电影长片到电视剧集，从剧情脉络到幕后故事，这里把散落的信息整理成可以慢慢阅读的页面，帮助观众在观看前后都能更完整地理解一部作品。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/category"
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                浏览影视分类
                <i className="ri-arrow-right-line text-base leading-none" />
              </Link>
              <Link
                to="/news"
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                阅读影视资讯
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Cozy%20editorial%20writing%20desk%20in%20a%20quiet%20studio%20with%20stacked%20film%20reels%2C%20a%20warm%20orange%20desk%20lamp%20and%20deep%20navy%20blue%20shadows%2C%20open%20notebooks%20and%20printed%20film%20posters%2C%20calm%20atmospheric%20photography%2C%20no%20text&width=900&height=675&seq=about-studio-01&orientation=landscape"
                alt="影视内容编辑工作室"
                title="关于女友的妈妈影视专区 内容编辑"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
            {VALUES.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200/80 bg-background-100 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-lg leading-none`} />
                </span>
                <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-y border-background-200/70 bg-background-100 py-10 md:py-14">
        <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">网站定位与服务说明</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              本站聚焦「女友的妈妈」相关影视作品的剧情介绍与影视资讯，服务对象是影视爱好者、在线观影用户与剧情信息查阅者。你可以在这里找到作品的完整剧情脉络、基本信息与推荐关联内容。
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {[
                { value: `${allWorks.length}`, label: "在列影视作品" },
                { value: `${allNews.length}`, label: "资讯与解读" },
                { value: "2", label: "影视分类栏目" },
                { value: "24h", label: "资讯持续更新" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200/80 bg-background-50 p-4">
                  <p className="font-heading text-xl font-bold text-primary-600">{stat.value}</p>
                  <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-lg bg-secondary-50 p-4">
              <p className="text-xs leading-relaxed text-foreground-700">
                本站为纯信息展示平台，<strong className="font-medium text-foreground-950">不提供影片播放、下载或任何付费服务</strong>。
                站内影视资料、剧情文字与资讯均为演示数据，不涉及真实个人信息。
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-7">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">给我们留言</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                想推荐作品、纠错剧情信息，或者对站点有建议？留下你的想法，我们会认真阅读每一条留言。
              </p>

              {status === "success" ? (
                <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-5 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-background-50">
                    <i className="ri-check-line text-xl leading-none" />
                  </span>
                  <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">留言提交成功</h3>
                  <p className="mt-1.5 text-sm text-foreground-600">感谢你的反馈，我们会尽快查看并回复相关内容。</p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    再写一条
                  </button>
                </div>
              ) : (
                <form
                  id="about-message-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-5 space-y-4"
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700">
                        你的称呼
                      </label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        maxLength={40}
                        placeholder="例如：影迷小林"
                        className="mt-1.5 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700">
                        邮箱
                      </label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        maxLength={80}
                        placeholder="name@example.com"
                        className="mt-1.5 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="about-topic" className="block text-xs font-medium text-foreground-700">
                      留言类型
                    </label>
                    <select
                      id="about-topic"
                      name="topic"
                      defaultValue="作品推荐"
                      className="mt-1.5 h-10 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    >
                      <option value="作品推荐">作品推荐</option>
                      <option value="剧情纠错">剧情纠错</option>
                      <option value="内容建议">内容建议</option>
                      <option value="合作咨询">合作咨询</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700">
                      留言内容
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="写下你想说的话（500 字以内）"
                      className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>

                  <input
                    className="form-field-alt"
                    type="text"
                    name="contact_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === "error" && (
                    <p className="rounded-md bg-accent-50 px-3 py-2 text-xs leading-relaxed text-accent-900">
                      {formError}
                    </p>
                  )}

                  <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-[11px] text-foreground-500">提交后我们会通过邮箱与你联系，请确保邮箱填写正确。</p>
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-base leading-none" />
                          提交中
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line text-base leading-none" />
                          提交留言
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div className="mt-4 flex flex-col gap-3 rounded-lg bg-background-50 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-mail-line text-base leading-none" />
                </span>
                <div>
                  <p className="text-xs font-medium text-foreground-950">联系邮箱</p>
                  <p className="mt-0.5 text-xs text-foreground-600">{contactEmail || "contact@example-demo.com"}</p>
                </div>
              </div>
              <input
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="填写你的邮箱用于回信"
                aria-label="回信邮箱"
                className="h-10 w-full rounded-md border border-background-300 bg-background-100 px-3 text-xs text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 sm:max-w-[240px]"
              />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}